import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { GovernanceListingSection } from '../governance/GovernanceListingSection'
import { ComplianceCenterMetricTile } from '../compliance-center/ComplianceCenterMetricTile'
import { ComplianceCenterListRow } from '../compliance-center/ComplianceCenterListRow'
import { Button } from '../ui/Button'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import {
  fetchStandardExecutiveDrilldown,
  fetchExecutiveHistory,
  type StandardExecutiveDrilldown,
} from '../../services/executiveGovernanceApi'

type Props = {
  standardCode: string
}

export default function ExecutiveStandardDrilldown({ standardCode }: Props) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [data, setData] = React.useState<StandardExecutiveDrilldown | null>(null)
  const [history, setHistory] = React.useState<Array<{ at: string; level: string }>>([])

  React.useEffect(() => {
    setLoading(true)
    setError(null)
    void Promise.all([fetchStandardExecutiveDrilldown(standardCode), fetchExecutiveHistory(standardCode, 10)])
      .then(([drill, hist]) => {
        setData(drill)
        setHistory(hist.stabilityTrend || [])
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load executive drill-down'))
      .finally(() => setLoading(false))
  }, [standardCode])

  if (loading) {
    return <p className="text-sm text-slate-500">{t('common.loading', 'Loading…')}</p>
  }
  if (error) {
    return <p className="text-sm text-red-600">{error}</p>
  }
  if (!data) return null

  return (
    <div className={listStyles.pageStack}>
      <GovernanceListingSection
        title={t('executiveGovernance.standardExecutiveView', 'Standard executive view')}
        description={t('executiveGovernance.standardDrilldownIntro', 'Executive posture for {{standard}}.', {
          standard: data.standardName || standardCode,
        })}
        toolbar={
          <Button type="button" size="small" color="outline" onClick={() => navigate('/compliance-center/executive-governance')}>
            {t('executiveGovernance.backToOrgView', 'Organization view')}
          </Button>
        }
      >
        <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid4}`}>
          <ComplianceCenterMetricTile
            label={t('executiveGovernance.certificationStability', 'Certification Stability')}
            value={data.certificationRiskLevel || '—'}
            tone="red"
          />
          <ComplianceCenterMetricTile
            label={t('executiveGovernance.readinessTrajectory', 'Readiness Trajectory')}
            value={data.readinessTrajectory || '—'}
            tone="orange"
          />
          <ComplianceCenterMetricTile
            label={t('executiveGovernance.operationalPressure', 'Operational Pressure')}
            value={Math.round((data.overallOperationalPressure ?? 0) * 100) / 100}
            tone="blue"
          />
          <ComplianceCenterMetricTile
            label={t('executiveGovernance.impactedSystems', 'Impacted systems')}
            value={data.impactedSystems?.length ?? 0}
            tone="purple"
          />
        </div>
      </GovernanceListingSection>

      {data.blastRadius?.impacts?.length ? (
        <GovernanceListingSection title={t('executiveGovernance.blastRadius', 'Blast Radius')}>
          <div className="flex flex-col gap-2">
            {data.blastRadius.impacts.slice(0, 8).map((impact) => (
              <ComplianceCenterListRow key={impact.controlCode} title={impact.controlCode} meta={impact.summary} />
            ))}
          </div>
        </GovernanceListingSection>
      ) : null}

      {data.degradedControls?.length ? (
        <GovernanceListingSection title={t('executiveGovernance.degradedControls', 'Degraded controls')}>
          <div className="flex flex-col gap-2">
            {data.degradedControls.map((c) => (
              <ComplianceCenterListRow
                key={c.id}
                title={c.controlCode || c.id}
                meta={`${c.controlHealth} · ${c.informationSystemName || '—'}`}
              />
            ))}
          </div>
        </GovernanceListingSection>
      ) : null}

      {data.recentOperationalEvents?.length ? (
        <GovernanceListingSection title={t('executiveGovernance.recentOperationalEvents', 'Recent operational events')}>
          <ul className="m-0 p-0 list-none space-y-2">
            {data.recentOperationalEvents.slice(0, 12).map((ev) => (
              <li key={ev.id} className="text-xs border border-slate-100 rounded p-2 bg-slate-50">
                <span className="font-medium">{ev.eventType}</span>
                {ev.severity ? ` · ${ev.severity}` : ''}
                {ev.createdAt ? ` · ${new Date(ev.createdAt).toLocaleString()}` : ''}
              </li>
            ))}
          </ul>
        </GovernanceListingSection>
      ) : null}

      {history.length > 1 ? (
        <GovernanceListingSection title={t('executiveGovernance.stabilityHistory', 'Stability history')}>
          <ul className="text-xs text-slate-600 m-0 p-0 list-none space-y-1">
            {history.map((h, i) => (
              <li key={i}>
                {new Date(h.at).toLocaleString()}: {h.level}
              </li>
            ))}
          </ul>
        </GovernanceListingSection>
      ) : null}

      {data.explanations?.lines?.length ? (
        <GovernanceListingSection title={t('executiveGovernance.explainability', 'Explainability')}>
          <ul className="text-xs text-slate-600 list-disc pl-4 m-0 space-y-1">
            {data.explanations.lines.map((line) => (
              <li key={line.code}>{line.message}</li>
            ))}
          </ul>
        </GovernanceListingSection>
      ) : null}
    </div>
  )
}
